import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";

type Stage = "precall" | "connecting" | "incall" | "ended" | "invalid";

function formatTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function VideoJoinPage() {
  const [params] = useSearchParams();
  const token = params.get("t")?.trim() ?? "";
  const hasValidToken = useMemo(() => /^[A-Za-z0-9_-]+$/.test(token), [token]);

  const [stage, setStage] = useState<Stage>(hasValidToken ? "precall" : "invalid");
  const [displayName, setDisplayName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [remoteParticipantCount, setRemoteParticipantCount] = useState(0);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [hasRemoteAudio, setHasRemoteAudio] = useState(false);

  const previewRef = useRef<HTMLDivElement | null>(null);
  const localRef = useRef<HTMLDivElement | null>(null);
  const remoteRef = useRef<HTMLDivElement | null>(null);
  const localTracksRef = useRef<any[]>([]);
  const roomRef = useRef<any | null>(null);
  const timerRef = useRef<number | null>(null);

  const cleanupTracks = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    localTracksRef.current.forEach((track) => {
      try {
        track.stop?.();
        track.detach?.().forEach((element: Element) => element.remove());
      } catch {
        // Ignore teardown failures from stale tracks.
      }
    });
    localTracksRef.current = [];

    if (previewRef.current) {
      previewRef.current.innerHTML = "";
    }
    if (localRef.current) {
      localRef.current.innerHTML = "";
    }
    if (remoteRef.current) {
      remoteRef.current.innerHTML = "";
    }

    roomRef.current = null;
    setRemoteParticipantCount(0);
    setHasRemoteVideo(false);
    setHasRemoteAudio(false);
  }, []);

  const attachTrack = useCallback((
    track: any,
    container: HTMLDivElement | null,
    options?: { mirror?: boolean },
  ) => {
    if (!container) {
      return;
    }

    const trackSid = typeof track.sid === "string" ? track.sid : "";
    if (trackSid) {
      container.querySelectorAll(`[data-track-sid="${trackSid}"]`).forEach((element) => element.remove());
    }

    const element = track.attach?.();
    if (!(element instanceof HTMLElement)) {
      return;
    }

    element.style.width = "100%";
    element.style.height = "100%";
    element.style.objectFit = "cover";
    if (track.kind === "video" && options?.mirror) {
      element.style.transform = "scaleX(-1)";
    }
    if (trackSid) {
      element.dataset.trackSid = trackSid;
    }
    container.appendChild(element);
  }, []);

  const refreshRemoteMediaState = useCallback((room: any | null = roomRef.current) => {
    let participants = 0;
    let video = false;
    let audio = false;

    room?.participants?.forEach((participant: any) => {
      participants += 1;
      participant.tracks.forEach((publication: any) => {
        if (!publication.isSubscribed || !publication.track) {
          return;
        }

        if (publication.track.kind === "video") {
          video = true;
        }
        if (publication.track.kind === "audio") {
          audio = true;
        }
      });
    });

    setRemoteParticipantCount(participants);
    setHasRemoteVideo(video);
    setHasRemoteAudio(audio);
  }, []);

  const syncTrackState = useCallback((kind: "audio" | "video", enabled: boolean) => {
    localTracksRef.current.forEach((track) => {
      if (track.kind !== kind) {
        return;
      }
      if (enabled) {
        track.enable?.();
      } else {
        track.disable?.();
      }
    });
  }, []);

  const startPreview = useCallback(async () => {
    if (!hasValidToken || localTracksRef.current.length > 0) {
      return;
    }

    try {
      const VideoSdk = await import("twilio-video");
      const tracks = await VideoSdk.createLocalTracks({ audio: true, video: { width: 640 } });
      localTracksRef.current = tracks;
      tracks.forEach((track) => {
        if (track.kind === "video") {
          attachTrack(track, previewRef.current, { mirror: true });
        }
      });
    } catch {
      setErrorMessage("Could not access camera/microphone. Please allow permissions and try again.");
    }
  }, [attachTrack, hasValidToken]);

  useEffect(() => {
    startPreview();
    return () => {
      roomRef.current?.disconnect?.();
      cleanupTracks();
    };
  }, [cleanupTracks, startPreview]);

  useEffect(() => {
    setStage((current) => {
      if (current === "connecting" || current === "incall" || current === "ended") {
        return current;
      }

      return hasValidToken ? "precall" : "invalid";
    });
  }, [hasValidToken]);

  useEffect(() => {
    if (stage !== "incall") {
      return;
    }

    if (previewRef.current) {
      previewRef.current.innerHTML = "";
    }

    localTracksRef.current.forEach((track) => {
      if (track.kind !== "video") {
        return;
      }

      track.detach?.().forEach((element: Element) => element.remove());
      attachTrack(track, localRef.current, { mirror: true });
    });

    roomRef.current?.participants?.forEach((participant: any) => {
      participant.tracks.forEach((publication: any) => {
        if (!publication.isSubscribed || !publication.track) {
          return;
        }

        attachTrack(publication.track, remoteRef.current);
      });
    });

    refreshRemoteMediaState(roomRef.current);
  }, [attachTrack, refreshRemoteMediaState, stage]);

  const handleParticipant = useCallback((participant: any) => {
    participant.tracks.forEach((publication: any) => {
      if (publication.isSubscribed && publication.track) {
        attachTrack(publication.track, remoteRef.current);
      }
    });
    refreshRemoteMediaState(roomRef.current);

    participant.on?.("trackSubscribed", (track: any) => {
      attachTrack(track, remoteRef.current);
      refreshRemoteMediaState(roomRef.current);
    });

    participant.on?.("trackUnsubscribed", (track: any) => {
      track.detach?.().forEach((element: Element) => element.remove());
      refreshRemoteMediaState(roomRef.current);
    });
  }, [attachTrack, refreshRemoteMediaState]);

  const handleJoin = useCallback(async () => {
    if (!hasValidToken) {
      setStage("invalid");
      return;
    }

    setErrorMessage("");
    setStage("connecting");

    try {
      const response = await fetch(
        `${API_BASE}/video-token-guest?t=${encodeURIComponent(token)}&name=${encodeURIComponent(displayName.trim() || "Patient")}`,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to get token");
      }

      if (localTracksRef.current.length === 0) {
        const VideoSdk = await import("twilio-video");
        localTracksRef.current = await VideoSdk.createLocalTracks({ audio: true, video: { width: 640 } });
      }

      const VideoSdk = await import("twilio-video");
      const room = await VideoSdk.connect(payload.token, {
        name: payload.roomName,
        tracks: localTracksRef.current,
      });

      roomRef.current = room;
      setStage("incall");
      refreshRemoteMediaState(room);
      setTimerSeconds(0);
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      timerRef.current = window.setInterval(() => {
        setTimerSeconds((value) => value + 1);
      }, 1000);

      room.participants.forEach((participant: any) => handleParticipant(participant));
      room.on("participantConnected", (participant: any) => {
        handleParticipant(participant);
        refreshRemoteMediaState(room);
      });
      room.on("participantDisconnected", (participant: any) => {
        participant.tracks.forEach((publication: any) => {
          if (publication.track) {
            publication.track.detach?.().forEach((element: Element) => element.remove());
          }
        });
        refreshRemoteMediaState(room);
      });
      room.on("disconnected", () => {
        cleanupTracks();
        setStage("ended");
      });

      syncTrackState("audio", micEnabled);
      syncTrackState("video", cameraEnabled);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to join the call. Please try again.");
      setStage("precall");
    }
  }, [cameraEnabled, cleanupTracks, displayName, handleParticipant, hasValidToken, micEnabled, refreshRemoteMediaState, syncTrackState, token]);

  const handleLeave = useCallback(() => {
    roomRef.current?.disconnect?.();
    cleanupTracks();
    setStage("ended");
  }, [cleanupTracks]);

  const toggleCamera = useCallback(() => {
    setCameraEnabled((enabled) => {
      const next = !enabled;
      syncTrackState("video", next);
      return next;
    });
  }, [syncTrackState]);

  const toggleMic = useCallback(() => {
    setMicEnabled((enabled) => {
      const next = !enabled;
      syncTrackState("audio", next);
      return next;
    });
  }, [syncTrackState]);

  const precallMessage = stage === "connecting"
    ? "Connecting you to your provider. Please keep this page open."
    : "Preview your camera, enter your name, then tap Join.";

  const remoteStatusMessage = remoteParticipantCount === 0
    ? "Waiting for provider to join..."
    : hasRemoteVideo
      ? null
      : hasRemoteAudio
        ? "Provider joined without video."
        : "Connecting provider video...";

  if (stage === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-center text-slate-100">
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold">Invalid video call link</h1>
          <p className="text-sm text-slate-400">This link is not valid. Please check the link you received and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {stage === "precall" || stage === "connecting" ? (
        <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-6 px-6 py-8">
          <h1 className="text-2xl font-semibold">Join Video Call</h1>
          <p className="max-w-md text-center text-sm text-slate-400">
            {precallMessage}
          </p>
          <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-slate-800">
            <div ref={previewRef} className="h-full w-full" />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleCamera}
              className={`rounded-full p-3 ${cameraEnabled ? "bg-slate-800 text-slate-100" : "bg-red-500 text-white"}`}
            >
              {cameraEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={toggleMic}
              className={`rounded-full p-3 ${micEnabled ? "bg-slate-800 text-slate-100" : "bg-red-500 text-white"}`}
            >
              {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
          </div>
          <input
            type="text"
            maxLength={50}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Your name (optional)"
            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-base outline-none ring-0 placeholder:text-slate-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={handleJoin}
            disabled={stage === "connecting"}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {stage === "connecting" ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {stage === "connecting" ? "Connecting..." : "Join Call"}
          </button>
          {errorMessage ? <p className="text-center text-sm text-red-400">{errorMessage}</p> : null}
        </div>
      ) : null}

      {stage === "incall" ? (
        <div className="flex min-h-screen flex-col">
          <div className="relative flex-1 overflow-hidden bg-slate-900">
            <div ref={remoteRef} className="h-full w-full" />
            {remoteStatusMessage ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">
                {remoteStatusMessage}
              </div>
            ) : null}
            <div className="absolute right-4 top-4 h-40 w-28 overflow-hidden rounded-xl border border-white/20 bg-slate-950">
              <div ref={localRef} className="h-full w-full" />
            </div>
          </div>
          <div className="bg-slate-800 py-2 text-center text-sm text-slate-400">{formatTimer(timerSeconds)}</div>
          <div className="flex items-center justify-center gap-4 bg-slate-800 px-6 py-5">
            <button
              type="button"
              onClick={toggleMic}
              className={`rounded-full p-4 ${micEnabled ? "bg-slate-700 text-slate-100" : "bg-red-500 text-white"}`}
            >
              {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={toggleCamera}
              className={`rounded-full p-4 ${cameraEnabled ? "bg-slate-700 text-slate-100" : "bg-red-500 text-white"}`}
            >
              {cameraEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={handleLeave}
              className="rounded-full bg-red-500 p-4 text-white"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : null}

      {stage === "ended" ? (
        <div className="flex min-h-screen items-center justify-center px-6 text-center">
          <div className="space-y-3">
            <h1 className="text-2xl font-semibold">Call Ended</h1>
            <p className="text-sm text-slate-400">The video call has ended. You may close this tab.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
